/*
Copyright 2021-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

// We need to import this somewhere, once, so that the correct 'request'
// function gets set. It needs to be not in the same file as we use
// createClient, or the typescript transpiler gets confused about
// dependency references.
import "./auth/cabinetInvite";
import "./auth/captureJoinHandoff";
import "matrix-js-sdk/lib/browser-index";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { logger } from "matrix-js-sdk/lib/logger";

import { App } from "./App";
import { init as initRageshake } from "./settings/rageshake";
import { Initializer } from "./initializer";
import { AppViewModel } from "./state/AppViewModel";
import { globalScope } from "./state/ObservableScope";

import { installZoomGuard } from "./utils/zoom";
const removeZoomGuard = installZoomGuard();
import.meta.hot?.dispose(removeZoomGuard);

import { CabinetInviteRedirect } from "./auth/CabinetInviteRedirect";
import { Config } from "./config/Config";
import { JoinBridgeFrame } from "./auth/JoinBridgeFrame";
import { StaffSessionBridge } from "./auth/StaffSessionBridge";

const root = createRoot(document.getElementById("root")!);
// Cross-origin account bridges must work without camera/microphone permissions.
// In Safari those permissions can hide mediaDevices entirely in an iframe.
// Do not initialize WebRTC, Matrix sync, media models or the full application.
const bridge = window.location.pathname === "/auth/bridge"
  ? JoinBridgeFrame
  : window.location.pathname === "/auth/staff-bridge"
    ? StaffSessionBridge
    : window.location.pathname === "/auth/invite"
      ? CabinetInviteRedirect
      : undefined;
if (bridge) {
  const Bridge = bridge;
  void Config.init().then(() => root.render(<Bridge />)).catch(() => {
    logger.warn("Account bridge configuration unavailable");
  });
} else {
initRageshake().catch((e) => {
  logger.error("Failed to initialize rageshake", e);
});

logger.info(`Element Call ${import.meta.env.VITE_APP_VERSION || "dev"}`);



let fatalError: Error | null = null;

if (!window.isSecureContext) {
  fatalError = new Error(
    "This app cannot run in an insecure context. To fix this, access the app " +
      "via a local loopback address, or serve it over HTTPS.\n" +
      "https://developer.mozilla.org/en-US/docs/Web/Security/Secure_Contexts",
  );
} else if (!navigator.mediaDevices) {
  fatalError = new Error("Your browser does not support WebRTC.");
}

if (fatalError !== null) {
  root.render(fatalError.message);
  throw fatalError; // Stop the app early
}

Initializer.initBeforeReact()
  .then(() => {
    root.render(
      <StrictMode>
        <App vm={new AppViewModel(globalScope)} />
      </StrictMode>,
    );
  })
  .catch((e) => {
    logger.error(`Failed to initialize app ${e.message}`, e);
    root.render(e.message);
  });

}
